<template>
  <div class="app">
    <template v-if="isAdmin">
      <CompanyBillingWarning v-if="company?.isBlocked" />
      <CompanyPublishWarning v-else-if="!company?.isPublished" />
    </template>

    <Cover
      v-if="isCoverVisible && company?.login"
      :is-main-view
      :covers-login="company.login"
      :logo-url="company.logoUrl"
      :sections="sectionsInfo"
      :header="{
        title: company.name,
        subtitle: company.headerSubtitle,
      }"
      :horizontal-menu="{
        items: horizontalMenuItems,
        showAddButton: horizontalMenuShowAddButton,
        addHandler: horizontalMenuAddHandler,
      }"
    >
      <template #userProfile>
        <ProfileMenu v-if="user" />
      </template>
    </Cover>

    <div
      v-else-if="user"
      :class="[
        'app__profile-menu',
        {
          'app__profile-menu--full-width': isEditEnabled && !company?.headerCustomPage,
        },
      ]"
    >
      <ProfileMenu />
    </div>

    <header
      v-if="company?.headerCustomPage"
      class="app__header"
    >
      <CustomPageModule
        :id="company.headerCustomPage.id"
        :custom-page="company.headerCustomPage"
      />
    </header>

    <main>
      <slot />
    </main>

    <footer class="footer">
      <CustomPageModule
        v-if="company?.footerCustomPage"
        :id="company.footerCustomPage.id"
        :custom-page="company.footerCustomPage"
      />

      <CoverFooter
        v-if="isCoverVisible && company?.login"
        :covers-login="company?.login"
      />
    </footer>

    <CommunicationChannel
      v-if="company?.communicationChannels?.length"
      :channels="company.communicationChannels"
      :channel-operator="company.communicationChannelOperators?.[0]"
    />

    <div id="modal" />

    <Teleport
      v-if="isMounted && isOpenAuthModal"
      to="#modal"
    >
      <Modal @close="setAuthModal(false)">
        <Auth
          :with-phone-auth="
            !!company?.authMethods?.find(({ authType }) => authType === Types.EAuthMethods.phone)
          "
          :with-motmom-auth="
            !!company?.authMethods?.find(({ authType }) => authType === Types.EAuthMethods.motmom)
          "
          :send-code-request="sendCode"
          :validate-code-request="validateCode"
          :fetch-user
          @close="setAuthModal(false)"
        />
      </Modal>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { LS_EDIT_ABILITTY } from '@/constants';

import { Types } from '@/entities/company';

import { Modal } from '@/shared/ui/modal';
import { Auth } from '@/shared/ui/auth';
import { modalStore } from '@/shared/composables';

import { CompanyPublishWarning, CompanyBillingWarning } from '@/widgets/company-warning';
import { CommunicationChannel } from '@/features/communication-channel';

import { CustomPageModule } from '@/widgets/custom-page';
import { ProfileMenu } from '@/widgets/cover';

interface IAttrs {
  ['horizontal-menu-show-add-button']: boolean;
  ['horizontal-menu-add-handler']: () => void;
}
const { horizontalMenuItems } = storeToRefs(useCoverStore());

const route = useRoute();

const attrs = useAttrs() as unknown as IAttrs;

const horizontalMenuShowAddButton = computed(
  () => attrs['horizontal-menu-show-add-button'] ?? false,
);
const horizontalMenuAddHandler = computed(() => attrs['horizontal-menu-add-handler'] ?? (() => {}));

const companyStore = useCompanyStore();

const { company } = storeToRefs(companyStore);

const { user, isAdmin, isEditEnabled } = storeToRefs(useUserStore());
const { setEditEnabled, sendCode, validateCode, fetch: fetchUser } = useUserStore();
const { isOpenAuthModal, setAuthModal } = modalStore;
const { isCoverModuleEnabled } = companyStore;

const isMounted = ref(false);

const isMainView = computed(() => route.name === 'main');

const nuxtApp = useNuxtApp();
if (!nuxtApp.$authModal) {
  nuxtApp.provide('authModal', {
    isOpen: isOpenAuthModal,
    setState: setAuthModal,
  });
}

onMounted(() => {
  isMounted.value = true;

  if (!user) {
    return;
  }

  const isEnabled = localStorage.getItem(LS_EDIT_ABILITTY);

  if (isEnabled) {
    setEditEnabled(JSON.parse(isEnabled));
  }
});

const { fetchAll } = useSectionStore();
const { sectionsInfo } = storeToRefs(useSectionStore());
fetchAll();

const isCoverVisible = computed(() => !route.meta.isModalPage && isCoverModuleEnabled);
</script>

<style lang="scss" scoped>
.app {
  display: flex;
  flex-direction: column;
  min-height: 100dvh;

  &__profile-menu {
    position: absolute;
    top: 0;
    right: 0px;
    padding-top: 12px;
    padding-right: 11px;
    pointer-events: none;

    &--full-width {
      height: 80px;
      position: relative;
      box-sizing: border-box;
    }
  }
}

main {
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>
